import { useState } from 'react';
import { FreeLesson } from '../data/freeLessonsData';

interface LessonViewerProps {
  lesson: FreeLesson;
  onClose: () => void;
}

export default function LessonViewer({ lesson, onClose }: LessonViewerProps) {
  const [activeTab, setActiveTab] = useState<'learn' | 'practice'>('learn');
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (qIndex: number, choiceIndex: number) => {
    if (showResults) return;
    setSelectedAnswers((prev) => ({ ...prev, [qIndex]: choiceIndex }));
  };

  const score = Object.entries(selectedAnswers).filter(
    ([qi, ans]) => lesson.questions[Number(qi)]?.correct === ans
  ).length;

  const allAnswered = Object.keys(selectedAnswers).length === lesson.questions.length;

  const difficultyColor =
    lesson.difficulty === 'Beginner'
      ? 'bg-green-100 text-green-700'
      : lesson.difficulty === 'Intermediate'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-rose-100 text-rose-700';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl relative">
        {/* Header */}
        <div className={`${lesson.bgColor} rounded-t-3xl px-8 py-7`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl shadow-md flex-shrink-0">
                <i className={`${lesson.icon} text-2xl ${lesson.color}`}></i>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${difficultyColor}`}>
                    {lesson.difficulty}
                  </span>
                  <span className="text-xs font-semibold text-gray-500 bg-white/70 px-3 py-1 rounded-full">
                    {lesson.grade}
                  </span>
                  <span className="text-xs font-semibold text-gray-500 bg-white/70 px-3 py-1 rounded-full">
                    <i className="ri-time-line mr-1"></i>{lesson.duration}
                  </span>
                </div>
                <h2 className="text-2xl font-black text-gray-900">{lesson.title}</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 transition-all cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-2 mt-5 bg-white/50 rounded-full p-1 w-fit">
            {(['learn', 'practice'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap capitalize ${
                  activeTab === tab
                    ? 'bg-white text-gray-900 shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'learn' ? '📖 Learn' : '✏️ Practice'}
              </button>
            ))}
          </div>
        </div>

        {/* Learn Tab */}
        {activeTab === 'learn' && (
          <div className="px-8 py-8 space-y-6">
            {/* Concept */}
            <div>
              <h3 className="text-lg font-black text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-flash-line text-amber-500 text-xl"></i>
                Concept Explanation
              </h3>
              <p className="text-gray-700 leading-relaxed text-base bg-gray-50 rounded-2xl p-5">
                {lesson.conceptExplanation}
              </p>
            </div>

            {/* Key Points */}
            <div>
              <h3 className="text-lg font-black text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-list-check-2 text-teal-500 text-xl"></i>
                Key Points to Remember
              </h3>
              <div className="space-y-2">
                {lesson.keyPoints.map((point, i) => (
                  <div key={i} className="flex items-start gap-3 bg-teal-50 rounded-xl px-5 py-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-teal-500 rounded-full flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs font-bold">{i + 1}</span>
                    </div>
                    <p className="text-gray-800 text-sm leading-relaxed">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Teacher Tip */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-2xl px-6 py-4 flex gap-3">
              <i className="ri-star-fill text-amber-500 text-xl flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="font-bold text-amber-800 text-sm mb-1">Teacher's Tip</p>
                <p className="text-amber-700 text-sm leading-relaxed">{lesson.tip}</p>
              </div>
            </div>

            <button
              onClick={() => setActiveTab('practice')}
              className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-black rounded-2xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap text-base shadow-lg"
            >
              Ready? Start Practice Questions →
            </button>
          </div>
        )}

        {/* Practice Tab */}
        {activeTab === 'practice' && (
          <div className="px-8 py-8">
            {!showResults ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-black text-gray-900">
                    Practice Questions
                    <span className="ml-2 text-sm font-semibold text-gray-400">
                      ({Object.keys(selectedAnswers).length}/{lesson.questions.length} answered)
                    </span>
                  </h3>
                  {/* Progress bar */}
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-500 rounded-full transition-all duration-300"
                      style={{ width: `${(Object.keys(selectedAnswers).length / lesson.questions.length) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-6">
                  {lesson.questions.map((q, qi) => (
                    <div key={qi} className="bg-gray-50 rounded-2xl p-6">
                      <p className="font-bold text-gray-900 mb-4 text-base">
                        <span className="text-teal-600 mr-2">Q{qi + 1}.</span>
                        {q.question}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {q.choices.map((choice, ci) => {
                          const isSelected = selectedAnswers[qi] === ci;
                          return (
                            <button
                              key={ci}
                              onClick={() => handleAnswer(qi, ci)}
                              className={`text-left px-5 py-3 rounded-xl border-2 transition-all duration-200 text-sm cursor-pointer ${
                                isSelected
                                  ? 'bg-teal-50 border-teal-500 text-teal-800 font-semibold'
                                  : 'bg-white border-gray-200 text-gray-700 hover:border-teal-300'
                              }`}
                            >
                              <span className="font-bold mr-2">{String.fromCharCode(65 + ci)}.</span>
                              {choice}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowResults(true)}
                  disabled={!allAnswered}
                  className="mt-8 w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-black rounded-2xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap text-base shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Check My Answers
                </button>
              </>
            ) : (
              <>
                {/* Score Banner */}
                <div className={`rounded-2xl p-6 mb-8 text-center ${
                  score === lesson.questions.length
                    ? 'bg-teal-50 border-2 border-teal-300'
                    : score >= lesson.questions.length * 0.7
                    ? 'bg-amber-50 border-2 border-amber-300'
                    : 'bg-rose-50 border-2 border-rose-300'
                }`}>
                  <div className="text-5xl mb-3">
                    {score === lesson.questions.length ? '🎉' : score >= lesson.questions.length * 0.7 ? '👍' : '📚'}
                  </div>
                  <p className="text-3xl font-black text-gray-900 mb-1">
                    {score} / {lesson.questions.length}
                  </p>
                  <p className={`text-lg font-bold ${
                    score === lesson.questions.length ? 'text-teal-700' :
                    score >= lesson.questions.length * 0.7 ? 'text-amber-700' : 'text-rose-700'
                  }`}>
                    {score === lesson.questions.length
                      ? 'Perfect Score! Outstanding work!'
                      : score >= lesson.questions.length * 0.7
                      ? 'Great job! Review the ones you missed.'
                      : 'Keep practicing — you\'re getting there!'}
                  </p>
                </div>

                {/* Answer Review */}
                <div className="space-y-5">
                  {lesson.questions.map((q, qi) => {
                    const isCorrect = selectedAnswers[qi] === q.correct;
                    return (
                      <div key={qi} className={`rounded-2xl p-5 border-2 ${isCorrect ? 'border-teal-200 bg-teal-50/50' : 'border-rose-200 bg-rose-50/50'}`}>
                        <div className="flex items-start gap-3 mb-3">
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${isCorrect ? 'bg-teal-500' : 'bg-rose-500'}`}>
                            <i className={`${isCorrect ? 'ri-check-line' : 'ri-close-line'} text-white text-sm`}></i>
                          </div>
                          <p className="font-bold text-gray-900 text-sm">Q{qi + 1}. {q.question}</p>
                        </div>
                        <div className="ml-10 space-y-1.5">
                          {q.choices.map((choice, ci) => (
                            <div
                              key={ci}
                              className={`px-4 py-2 rounded-lg text-sm ${
                                ci === q.correct
                                  ? 'bg-teal-100 text-teal-800 font-semibold'
                                  : selectedAnswers[qi] === ci && ci !== q.correct
                                  ? 'bg-rose-100 text-rose-700 line-through'
                                  : 'text-gray-500'
                              }`}
                            >
                              <span className="font-bold mr-2">{String.fromCharCode(65 + ci)}.</span>
                              {choice}
                              {ci === q.correct && <span className="ml-2 text-teal-600">✓ Correct</span>}
                            </div>
                          ))}
                          <div className="mt-3 bg-white rounded-xl px-4 py-3 text-sm text-gray-700 border border-gray-200">
                            <span className="font-bold text-gray-900">Explanation: </span>
                            {q.explanation}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 flex gap-3">
                  <button
                    onClick={() => { setShowResults(false); setSelectedAnswers({}); }}
                    className="flex-1 py-4 bg-gray-100 text-gray-700 font-bold rounded-2xl hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => { setActiveTab('learn'); setShowResults(false); setSelectedAnswers({}); }}
                    className="flex-1 py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold rounded-2xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Review Lesson
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
